import React, { useEffect, useState } from "react";
import { Loader2, Printer, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { aed } from "@/lib/format";
import { cn } from "@/lib/utils";

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function formatPaidAt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function PosBillReprintPopup({
  open,
  dateFrom,
  dateTo,
  search,
  onDateFromChange,
  onDateToChange,
  onSearchChange,
  bills,
  loading,
  pending,
  lastPaidBillId,
  onReprint,
  onClose,
}) {
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      return;
    }
    if (lastPaidBillId && bills.some((b) => b.id === lastPaidBillId)) {
      setSelectedId(lastPaidBillId);
      return;
    }
    setSelectedId(bills[0]?.id ?? null);
  }, [open, bills, lastPaidBillId]);

  if (!open) return null;

  const selected = bills.find((b) => b.id === selectedId) || null;

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-brand-brown/20 backdrop-blur-[2px] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pos-bill-reprint-title"
      data-testid="pos-bill-reprint-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col rounded-lg border bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="border-b px-4 py-3">
          <h2 id="pos-bill-reprint-title" className="text-lg font-semibold text-slate-900">
            Reprint bill
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Search paid bills by date, bill number, or customer.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-slate-600 mb-1 block">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                className="h-9"
                data-testid="pos-reprint-date-from"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-600 mb-1 block">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                className="h-9"
                data-testid="pos-reprint-date-to"
              />
            </div>
            <div className="col-span-2 relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Bill number, customer, table…"
                className="h-9 pl-8"
                autoFocus
                data-testid="pos-reprint-search"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading && bills.length === 0 && (
            <p className="px-4 py-8 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading bills…
            </p>
          )}
          {bills.map((b) => (
            <button
              key={b.id}
              type="button"
              className={cn(
                "w-full text-left px-4 py-3 text-sm border-b hover:bg-slate-50",
                selectedId === b.id && "bg-blue-50",
                lastPaidBillId === b.id && selectedId !== b.id && "bg-amber-50/60",
              )}
              onClick={() => setSelectedId(b.id)}
              data-testid={`pos-reprint-bill-${b.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono font-semibold text-slate-900">{b.bill_number}</span>
                <span className="font-mono font-bold">{aed(b.net_amount)}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {[
                  b.order_type_label || null,
                  b.customer_name,
                  b.table_code ? `Table ${b.table_code}` : null,
                  formatPaidAt(b.paid_at),
                ]
                  .filter(Boolean)
                  .join(" · ")}
                {lastPaidBillId === b.id ? " · Last paid" : ""}
              </div>
            </button>
          ))}
          {!loading && bills.length === 0 && (
            <p className="px-4 py-8 text-sm text-slate-500 text-center">No paid bills in this date range</p>
          )}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t">
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            disabled={!selected || pending}
            data-testid="pos-reprint-confirm"
            onClick={() => onReprint(selected)}
          >
            {pending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Printer size={14} className="mr-1.5" />}
            Reprint {selected ? selected.bill_number : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}

export { todayYmd };
